<template>
  <LoginSignupContainer :hide-image="!!templateId">
    <div class="signup-container">
      <div>
        <FriedParagraph class="branding">{{ t('create-account') }}</FriedParagraph>
        <FriedH2 class="signup-title branding">{{ title || t('welcome-to-getaccept') }}</FriedH2>
      </div>
      <div class="social-signup-container">
        <FriedButton
          data-external="sign-up-with-google-button"
          class="identity-provider-btn margin-bottom"
          button-type="tertiary"
          @click="signupExternal(IdentityProvider.Google)"
        >
          <template #leftIcon>
            <GoogleIcon class="icon" />
          </template>
          {{ t('sign-up-with-google') }}
        </FriedButton>
      </div>
      <FriedParagraph class="or-divider">{{ t('or') }}</FriedParagraph>
      <template v-if="!!error">
        <FriedMessage v-if="!!error" class="error" :title="error.title" type="error">
          <i18n-t v-if="!!error.variables?.link" keypath="please-try-again-or-contact-support">
            <template #contact-support-link>
              <FriedLink
                class="contact-support"
                :normal="true"
                href="http://help.getaccept.com/"
                target="_blank"
              >
                {{ t('contact-support') }}
              </FriedLink>
            </template>
          </i18n-t>
          <i18n-t v-else-if="!!error.variables?.didYouMean" keypath="did-you-mean">
            <template #did-you-mean-link>
              <FriedLink :normal="true" @click="emailInput = didYouMean">
                {{ didYouMean }}
              </FriedLink>
            </template>
          </i18n-t>
          <template v-else>{{ error.description }}</template>
        </FriedMessage>
      </template>
      <form
        id="signup-form"
        ref="signupFormRef"
        data-external="signup-form"
        @submit.prevent="signupEmail"
      >
        <FriedInput
          v-model.trim="emailInput"
          autofocus
          autocomplete="user-email"
          :label="t('work-email')"
          class="signup-input"
          :error-message="emailError"
          data-external="signup-email-input"
        />
        <FriedButton
          type="submit"
          :disabled="!emailInput"
          :loading="isLoading"
          class="signup-button"
          button-type="branding"
          data-external="signup-submit-button"
        >
          {{ buttonText || t('sign-up') }}
        </FriedButton>
      </form>
      <FriedParagraph class="terms" size="small">
        <i18n-t keypath="agree-to-terms-and-conditions_v2">
          <template #termsConditionLink>
            <FriedLink
              class="bold"
              href="https://www.getaccept.com/terms-and-conditions"
              target="_blank"
            >
              {{ t('terms-and-condition') }}
            </FriedLink>
          </template>
          <template #privacyPolicyLink>
            <FriedLink class="bold" href="https://www.getaccept.com/privacy-policy" target="_blank">
              {{ t('privacy-policy') }}
            </FriedLink>
          </template>
        </i18n-t>
      </FriedParagraph>
      <FriedParagraph class="sign-in-link">
        {{ t('already-have-an-account') }}
        <FriedLink class="bold" :href="isLocalHost ? '/login' : '/'">{{ t('sign-in') }}</FriedLink>
      </FriedParagraph>
    </div>
  </LoginSignupContainer>
</template>

<script lang="ts" setup>
import { AnalyticsEvent, AnalyticsUserTrait } from '@getaccept/lib-shared/src/types/analytics';
import { sanitizeEmail, validateEmail } from '@getaccept/lib-shared/src/helpers';
import LoginSignupContainer from '@getaccept/lib-shared/src/login-signup/components/LoginSignupContainer.vue';
import { Language } from '@getaccept/lib-shared/src/languages/enums/language';
import { onMounted, computed, ref, watch, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useEventListener } from '@vueuse/core';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import * as LocalStorageHelper from '@getaccept/lib-shared/src/helpers/local-storage.helper';
import { LocalStorageKey } from '@getaccept/lib-shared/src/enums/local-storage';
import { useEnvironment } from '@getaccept/lib-shared/src/composables/environment.composable';
import GoogleIcon from '../assets/google.svg';
import { useLanguageStore } from '../../language/store/language.store';
import { IdentityProvider } from '../enums/identity-provider';
import * as IdentityProviderHelper from '../helpers/identity-provider.helper';
import * as SignupHelper from '../helpers/signup.helper';
import { getUTMData } from '../helpers/get-utm-data.helper';
import { useGeoLocation } from '../../location/composables/geo-location.composable';
import { useSignup } from '../composables/signup.composable';
import { useSignupExternal } from '../external/composables/signup-external.composable';

const props = withDefaults(
  defineProps<{
    templateId?: string;
    isModal?: boolean;
    title?: string;
    buttonText?: string;
    registrationSource?: string;
    registrationCampaignId?: string;
    registrationEmail?: string;
  }>(),
  {
    isModal: false,
    title: '',
    buttonText: '',
    registrationSource: 'signup',
    registrationCampaignId: '',
    registrationEmail: '',
    templateId: '',
  }
);

const { t } = useI18n();
const signupFormRef = ref({ id: 'signup-form' });

const emailInput = ref('');
const email = computed(() => sanitizeEmail(emailInput.value));

const emailError = ref('');
const emailErrorMessage = computed(() => {
  if (!email.value) {
    return t('email-is-required');
  }
  return validateEmail(email.value) ? '' : t('enter-a-valid-email');
});

watch(emailInput, () => {
  if (!emailError.value) return;

  if (emailInput.value?.length) {
    emailError.value = '';
  }
});

const { language } = storeToRefs(useLanguageStore());
const { countryName } = useGeoLocation();

const { isLocalHost } = useEnvironment();

const { execute: signup, isLoading, didYouMean, error: emailRegistrationError } = useSignup();

const { initialize, validateIdentityProviderMessage, error: externalError } = useSignupExternal();

const error = computed(() => emailRegistrationError.value || externalError.value);

const payload = computed(() => ({
  email: email.value,
  registrationSource: props.registrationSource,
  templateId: props.templateId,
  registrationCampaign: props.registrationCampaignId,
  language: language.value,
  countryName: countryName.value,
}));

const signupEmail = () => {
  if (emailErrorMessage.value) {
    emailError.value = emailErrorMessage.value;
    return;
  }
  signup(0, { ...payload.value });
};

const signupExternal = async (provider: IdentityProvider) => {
  initialize(provider, payload.value);
};

const validatePostMessage = async (event: MessageEvent) => {
  const url = IdentityProviderHelper.getMessageUrl();
  if (!IdentityProviderHelper.validateMessageAndOrigin(event, url)) return;

  await validateIdentityProviderMessage({ event, url });
};

useEventListener(window, 'message', validatePostMessage);

const setInitAnalytics = () => {
  const utmData = getUTMData();
  const analyticsData = {
    [AnalyticsUserTrait.Browser]: navigator.userAgent,
    [AnalyticsUserTrait.SignupStep]: 0,
    ...utmData,
  };
  SignupHelper.setAnalyticsSignup(analyticsData, AnalyticsEvent.SignupLoaded);
};

onMounted(() => {
  setInitAnalytics();
  if (language.value !== Language.En) {
    signupFormRef.value.id = `signup-form-${language.value}`;
  }
  if (props.registrationEmail) {
    try {
      emailInput.value = atob(props.registrationEmail);
    } catch (e) {
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
    }
  }
});

onBeforeMount(() => {
  LocalStorageHelper.reset(LocalStorageKey.VerificationEmail);
  LocalStorageHelper.reset(LocalStorageKey.SignupAnalyticsData);
});
</script>

<style scoped>
.signup-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-150);
}

.social-signup-container {
  display: flex;
  flex-direction: column;

  .identity-provider-btn {
    margin: var(--spacing-25) 0;
  }
}

.signup-input {
  width: 100%;
}

.signup-button {
  margin-top: var(--spacing-100);
  width: 100%;
}

.or-divider {
  width: 100%;
  text-align: center;
  display: flex;
  gap: var(--spacing-100);
  align-items: center;
  color: var(--gray-39);

  &::after,
  &::before {
    content: '';
    flex: 1 1 auto;
    border-bottom: solid 1px var(--gray-89);
  }
}

.bold {
  font-weight: 600;
}

.error {
  margin-bottom: var(--spacing-100);

  .contact-support {
    display: contents;
  }
}

.terms,
.sign-in-link {
  color: var(--text-gray);
}
</style>
